/*************************************************/
/* MAP                                           */
/*************************************************/

export function map() {
    const componentSelector = '.map-component';
    const mapContainerSelector = '.map-component__map-image';
    const textSelector = '.map-component__text';
    const markerSelector = '.map-component__marker';
    const popupSelector = '.map-component__popup';
    const popupCloseSelector = '.map-component__popup-close';
    const overlaySelector = '.map-component__overlay';
    const markerActiveClass = 'map-component__marker--active';
    const popupActiveClass = 'map-component__popup--active';
    const popupFlipClass = 'map-component__popup--flip';
    const overlayActiveClass = 'map-component__overlay--active';
    const componentPopupActiveClass = 'map-component--popup-active';
    const bodyPopupOpenClass = 'map-popup-open';
    const popupShiftXProperty = '--map-popup-shift-x';
    const popupShiftYProperty = '--map-popup-shift-y';
    const textHeightProperty = '--map-text-height';
    const mapItemIdAttribute = 'data-map-item-id';
    const edgeGap = 15;
    const desktopBreakpoint = 992;

    const getMarker = (container, id) => container.querySelector(`${markerSelector}[${mapItemIdAttribute}="${id}"]`);
    const getPopup = (container, id) => container.querySelector(`${popupSelector}[${mapItemIdAttribute}="${id}"]`);
    const getComponent = (container) => container.closest(componentSelector);

    const setOverlay = (container, show) => {
        const component = getComponent(container);
        if (!component) return;

        const overlay = component.querySelector(overlaySelector);
        if (overlay) {
            overlay.classList.toggle(overlayActiveClass, show);
        }

        component.classList.toggle(componentPopupActiveClass, show);
        document.body.classList.toggle(bodyPopupOpenClass, show);
    };

    const closePopups = (container, focusMarker = false) => {
        const activeMarker = container.querySelector(`.${markerActiveClass}`);

        setOverlay(container, false);

        container.querySelectorAll(markerSelector).forEach(marker => {
            marker.classList.remove(markerActiveClass);
            marker.setAttribute('aria-expanded', 'false');
        });

        container.querySelectorAll(popupSelector).forEach(popup => {
            popup.classList.remove(popupActiveClass, popupFlipClass);
            popup.setAttribute('aria-hidden', 'true');
            popup.style.removeProperty(popupShiftXProperty);
            popup.style.removeProperty(popupShiftYProperty);
        });

        if (focusMarker && activeMarker) {
            activeMarker.focus();
        }
    };

    const positionPopup = (container, popup) => {
        popup.classList.remove(popupFlipClass);
        popup.style.removeProperty(popupShiftXProperty);
        popup.style.removeProperty(popupShiftYProperty);

        if (window.innerWidth <= desktopBreakpoint) return;

        const containerRect = container.getBoundingClientRect();
        let popupRect = popup.getBoundingClientRect();

        let shiftY = 0;
        if (popupRect.top < containerRect.top + edgeGap) {
            shiftY = (containerRect.top + edgeGap) - popupRect.top;
        } else if (popupRect.bottom > containerRect.bottom - edgeGap) {
            shiftY = (containerRect.bottom - edgeGap) - popupRect.bottom;
        }
        popup.style.setProperty(popupShiftYProperty, `${shiftY}px`);

        if (popupRect.right + edgeGap > containerRect.right) {
            popup.classList.add(popupFlipClass);
        }

        popupRect = popup.getBoundingClientRect();

        let shiftX = 0;
        if (popupRect.left < containerRect.left + edgeGap) {
            shiftX = (containerRect.left + edgeGap) - popupRect.left;
        } else if (popupRect.right > containerRect.right - edgeGap) {
            shiftX = (containerRect.right - edgeGap) - popupRect.right;
        }
        popup.style.setProperty(popupShiftXProperty, `${shiftX}px`);
    };

    const updateTextHeight = (component) => {
        if (window.innerWidth > desktopBreakpoint) {
            component.style.removeProperty(textHeightProperty);
            return;
        }

        const textEl = component.querySelector(textSelector);
        component.style.setProperty(textHeightProperty, `${textEl ? textEl.offsetHeight : 0}px`);
    };

    const openPopup = (container, id) => {
        const marker = getMarker(container, id);
        const popup = getPopup(container, id);
        if (!marker || !popup) return;

        const wasActive = marker.classList.contains(markerActiveClass);
        closePopups(container);
        if (wasActive) return;

        marker.classList.add(markerActiveClass);
        marker.setAttribute('aria-expanded', 'true');
        popup.classList.add(popupActiveClass);
        popup.setAttribute('aria-hidden', 'false');

        setOverlay(container, true);

        positionPopup(container, popup);
    };

    const init = function () {
        const containers = document.querySelectorAll(mapContainerSelector);
        if (!containers.length) return;

        const components = document.querySelectorAll(componentSelector);
        components.forEach(updateTextHeight);

        containers.forEach(container => {
            container.querySelectorAll(markerSelector).forEach(marker => {
                marker.addEventListener('click', e => {
                    e.stopPropagation();
                    openPopup(container, marker.getAttribute(mapItemIdAttribute));
                });
            });

            container.querySelectorAll(popupCloseSelector).forEach(closeButton => {
                closeButton.addEventListener('click', e => {
                    e.stopPropagation();
                    closePopups(container, true);
                });
            });
        });

        document.addEventListener('click', e => {
            containers.forEach(container => {
                const clickedMarkerOrPopup = e.target.closest(markerSelector) || e.target.closest(popupSelector);
                if (!container.contains(e.target) || !clickedMarkerOrPopup) {
                    closePopups(container);
                }
            });
        });

        document.addEventListener('keyup', e => {
            if (e.key === 'Escape') {
                containers.forEach(container => closePopups(container, true));
            }
        });

        window.addEventListener('resize', () => {
            components.forEach(updateTextHeight);

            containers.forEach(container => {
                const activePopup = container.querySelector(`.${popupActiveClass}`);
                if (activePopup) {
                    positionPopup(container, activePopup);
                }
            });
        });
    };

    init();
}

